import { musicController } from "./musicController";
import { supabase } from "@/integrations/supabase/client";
import {
  getMoment,
  type InstructorVoiceMoment,
} from "./instructorVoiceMap";
import { isApprovedInstructor, approvedVoiceIdForInstructor } from "./approvedVoices";
import { getErrorMessage } from "@/lib/errors";
import type { AnyValue } from "@/lib/rowTypes";

export type InstructorVoicePlayStatus =
  | "played"
  | "suppressed"
  | "already_played"
  | "queued_autoplay"
  | "generating_timeout"
  | "unavailable"
  | "failed"
  | "unknown_moment"
  | "blocked_unapproved_voice";

export interface InstructorVoicePlayResult {
  momentId: string;
  status: InstructorVoicePlayStatus;
  reason?: string;
  backendStatus?: string;
  cached?: boolean;
  autoplayBlocked?: boolean;
}

interface VoiceUrlResolution {
  url: string | null;
  status: string;
  cached: boolean;
  error?: string;
}

const POLL_INTERVAL_MS = 900;
const MAX_READY_WAIT_MS = 10_800;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function isInstructorAudioDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      import.meta.env.DEV ||
      params.get("audioDebug") === "1" ||
      window.localStorage.getItem("heirwise.audioDebug") === "1"
    );
  } catch {
    return import.meta.env.DEV;
  }
}

export function shouldShowInstructorVoiceQaControls(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const params = new URLSearchParams(window.location.search);
    return (
      import.meta.env.DEV ||
      params.get("audioDebug") === "1" ||
      window.location.hostname.includes("id-preview--") ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
    );
  } catch {
    return import.meta.env.DEV;
  }
}

/**
 * Singleton instructor-voice controller.
 *
 * - Fire-once per session per momentId. No replay on rerender/scroll.
 * - One active voice at a time. Starting a new line stops the previous.
 * - Music ducked (~45%) while voice plays; restored on end/error.
 * - 250ms intentional delay so voice doesn't collide with click SFX.
 * - Caches signed URL per (instructor, transcript) so repeat plays
 *   skip the edge-function round-trip.
 * - Soft-fails silently — if generation/caching is unavailable, the
 *   learner experience continues uninterrupted.
 */
class InstructorVoiceController {
  private played = new Set<string>();
  private inflight = new Map<string, Promise<VoiceUrlResolution>>();
  private playAttempts = new Map<string, Promise<InstructorVoicePlayResult>>();
  private urlCache = new Map<string, string>();
  private queuedAutoplay = new Set<string>();
  private current: HTMLAudioElement | null = null;
  private releaseDuck: ((ms?: number) => void) | null = null;
  private currentMomentId: string | null = null;
  private gestureListenersReady = false;

  private enabled = true;
  private volume = 0.9;
  private muted = false;

  // Listeners for UI state (current speaking moment)
  private listeners = new Set<() => void>();

  setEnabled(v: boolean) { this.enabled = v; if (!v) this.stop(); }
  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.current) try { this.current.volume = this.volume; } catch { /* ignore */ }
  }
  setMuted(m: boolean) { this.muted = m; if (m) this.stop(); }

  subscribe(fn: () => void) {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }
  private notify() { this.listeners.forEach((f) => { try { f(); } catch { /* ignore */ } }); }

  getSpeakingMomentId(): string | null { return this.currentMomentId; }

  /** Clear fire-once tracking. Call on logout/module reset for QA. */
  reset() { this.played.clear(); this.queuedAutoplay.clear(); }

  private debug(event: string, payload: Record<string, AnyValue>) {
    if (!isInstructorAudioDebugEnabled()) return;
    console.info("[instructor-audio]", event, payload);
  }

  private ensureGestureListeners() {
    if (this.gestureListenersReady || typeof window === "undefined") return;
    this.gestureListenersReady = true;
    const flush = () => { void this.flushQueuedAutoplay(); };
    window.addEventListener("pointerdown", flush, { capture: true, passive: true });
    window.addEventListener("touchend", flush, { capture: true, passive: true });
    window.addEventListener("keydown", flush, { capture: true });
    window.addEventListener("click", flush, { capture: true, passive: true });
  }

  private async flushQueuedAutoplay() {
    if (!this.queuedAutoplay.size) return;
    const pending = Array.from(this.queuedAutoplay);
    this.queuedAutoplay.clear();
    this.debug("autoplay_queue_flush", { pending });
    for (const momentId of pending) {
      void this.play(momentId, { fromAutoplayQueue: true, skipDelay: true });
    }
  }

  /**
   * Warm the signed-URL cache for a set of moments without playing them.
   * Safe to call repeatedly on module mount — dedupes via inflight map and
   * skips moments whose URL is already cached. Soft-fails silently.
   */
  async preload(momentIds: string[]): Promise<void> {
    this.ensureGestureListeners();
    const moments = momentIds
      .map((id) => getMoment(id))
      .filter((m): m is InstructorVoiceMoment => !!m);
    let nextIndex = 0;
    const workerCount = Math.min(2, moments.length);
    const workers = Array.from({ length: workerCount }, async () => {
      while (nextIndex < moments.length) {
        const moment = moments[nextIndex++];
        const result = await this.resolveUrl(moment, { poll: false, preload: true }).catch(() => null);
        this.debug("preload_result", {
          momentId: moment.momentId,
          moduleId: moment.moduleId,
          instructor: moment.instructor,
          audioPreloadStatus: result?.status ?? "failed",
          signedUrlExists: Boolean(result?.url),
          cacheStatus: result?.cached ? "cached" : result?.url ? "warmed" : "not_ready",
          firedState: this.played.has(moment.momentId),
        });
      }
    });
    await Promise.all(workers);
  }

  stop() {
    if (this.current) {
      try { this.current.pause(); this.current.currentTime = 0; } catch { /* ignore */ }
      this.current = null;
    }
    if (this.releaseDuck) { this.releaseDuck(300); this.releaseDuck = null; }
    if (this.currentMomentId) {
      this.currentMomentId = null;
      this.notify();
    }
  }

  async play(
    momentId: string,
    options: { fromAutoplayQueue?: boolean; skipDelay?: boolean } = {},
  ): Promise<InstructorVoicePlayResult> {
    this.ensureGestureListeners();
    const moment = getMoment(momentId);
    if (!moment) {
      this.debug("play_denied_unknown_moment", { momentId });
      return { momentId, status: "unknown_moment" };
    }
    // Defense-in-depth: refuse to request playback for an instructor slug
    // that does not map to an approved ElevenLabs voice ID. Server is the
    // ultimate source of truth, but rejecting client-side keeps unapproved
    // voices out of the request pipeline entirely and prevents the "played"
    // set from being polluted with a moment whose voice is not sanctioned.
    if (!isApprovedInstructor(moment.instructor)) {
      console.warn("[instructor-audio] blocked unapproved voice for instructor", {
        momentId,
        instructor: moment.instructor,
        resolvedVoiceId: approvedVoiceIdForInstructor(moment.instructor),
      });
      return { momentId, status: "blocked_unapproved_voice" };
    }
    if (!this.enabled || this.muted) {
      this.played.add(momentId);
      this.debug("play_suppressed", {
        momentId,
        moduleId: moment.moduleId,
        instructor: moment.instructor,
        enabled: this.enabled,
        muted: this.muted,
        volume: this.volume,
        firedState: this.played.has(momentId),
      });
      return { momentId, status: "suppressed", reason: !this.enabled ? "disabled" : "muted" };
    }
    if (this.played.has(momentId)) {
      this.debug("play_skipped_already_fired", {
        momentId,
        moduleId: moment.moduleId,
        instructor: moment.instructor,
        firedState: true,
      });
      return { momentId, status: "already_played" };
    }

    const existingAttempt = this.playAttempts.get(momentId);
    if (existingAttempt) return existingAttempt;

    const attempt = this.playInternal(moment, options).finally(() => {
      this.playAttempts.delete(momentId);
    });
    this.playAttempts.set(momentId, attempt);
    return attempt;
  }

  private async playInternal(
    moment: InstructorVoiceMoment,
    options: { fromAutoplayQueue?: boolean; skipDelay?: boolean },
  ): Promise<InstructorVoicePlayResult> {
    const momentId = moment.momentId;

    this.debug("play_requested", {
      momentId,
      moduleId: moment.moduleId,
      instructor: moment.instructor,
      enabled: this.enabled,
      volume: this.volume,
      muted: this.muted,
      cacheStatus: this.hasCachedUrl(moment) ? "memory" : "empty",
      firedState: this.played.has(momentId),
      fromAutoplayQueue: Boolean(options.fromAutoplayQueue),
    });

    // 250ms breathing room so click SFX lands first.
    if (!options.skipDelay) await wait(250);
    if (!this.enabled || this.muted) {
      this.played.add(momentId);
      this.debug("play_suppressed_after_delay", {
        momentId,
        moduleId: moment.moduleId,
        instructor: moment.instructor,
        enabled: this.enabled,
        muted: this.muted,
        volume: this.volume,
        firedState: this.played.has(momentId),
      });
      return { momentId, status: "suppressed", reason: !this.enabled ? "disabled" : "muted" };
    }

    const resolved = await this.resolveUrl(moment, { poll: true, preload: false });
    this.debug("playback_url_resolved", {
      momentId,
      moduleId: moment.moduleId,
      instructor: moment.instructor,
      enabled: this.enabled,
      volume: this.volume,
      muted: this.muted,
      cacheStatus: resolved.cached ? "cached" : resolved.url ? "fresh" : "miss",
      backendResponseStatus: resolved.status,
      generationStatus: resolved.status,
      signedUrlExists: Boolean(resolved.url),
      firedState: this.played.has(momentId),
    });
    if (!resolved.url) {
      const timedOut = resolved.status === "generating" || resolved.status === "generating_timeout";
      return {
        momentId,
        status: timedOut ? "generating_timeout" : "unavailable",
        backendStatus: resolved.status,
        cached: resolved.cached,
      };
    }
    if (!this.enabled || this.muted) {
      this.played.add(momentId);
      return { momentId, status: "suppressed", reason: !this.enabled ? "disabled" : "muted", backendStatus: resolved.status };
    }

    // Stop any currently-speaking line — no stacking.
    this.stop();

    let audio: HTMLAudioElement;
    try {
      audio = new Audio(resolved.url);
      audio.volume = this.volume;
      audio.preload = "auto";
    } catch (e: unknown) {
      this.debug("audio_create_failed", {
        momentId,
        moduleId: moment.moduleId,
        instructor: moment.instructor,
        audioPlayStarted: false,
        audioPlayError: String(getErrorMessage(e) ?? e),
        firedState: this.played.has(momentId),
      });
      return { momentId, status: "failed", backendStatus: resolved.status, cached: resolved.cached };
    }

    this.current = audio;
    this.currentMomentId = momentId;
    this.releaseDuck = musicController.pushDuck(0.45, 250);
    this.notify();

    const release = () => {
      if (this.current === audio) {
        this.current = null;
        this.currentMomentId = null;
        if (this.releaseDuck) { this.releaseDuck(400); this.releaseDuck = null; }
        this.notify();
      }
    };
    audio.addEventListener("ended", release);
    audio.addEventListener("error", release);
    try {
      const p = audio.play();
      if (p && typeof p.then === "function") await p;
      this.played.add(momentId);
      this.debug("audio_play_started", {
        momentId,
        moduleId: moment.moduleId,
        instructor: moment.instructor,
        enabled: this.enabled,
        volume: this.volume,
        muted: this.muted,
        cacheStatus: resolved.cached ? "cached" : "fresh",
        backendResponseStatus: resolved.status,
        signedUrlExists: true,
        generationStatus: resolved.status,
        audioPlayStarted: true,
        audioPlayError: null,
        autoplayBlocked: false,
        firedState: this.played.has(momentId),
      });
      return { momentId, status: "played", backendStatus: resolved.status, cached: resolved.cached };
    } catch (e: unknown) {
      const message = String(getErrorMessage(e) ?? e ?? "");
      const name = String((e as AnyValue)?.name ?? "");
      const autoplayBlocked =
        name === "NotAllowedError" ||
        /autoplay|user activation|gesture|permission/i.test(message);
      release();
      if (autoplayBlocked) {
        this.queuedAutoplay.add(momentId);
        this.debug("autoplay_blocked", {
          momentId,
          moduleId: moment.moduleId,
          instructor: moment.instructor,
          enabled: this.enabled,
          volume: this.volume,
          muted: this.muted,
          cacheStatus: resolved.cached ? "cached" : "fresh",
          backendResponseStatus: resolved.status,
          signedUrlExists: true,
          generationStatus: resolved.status,
          audioPlayStarted: false,
          audioPlayError: message || name,
          autoplayBlocked: true,
          firedState: this.played.has(momentId),
        });
        return {
          momentId,
          status: "queued_autoplay",
          backendStatus: resolved.status,
          cached: resolved.cached,
          autoplayBlocked: true,
        };
      }
      this.debug("audio_play_failed", {
        momentId,
        moduleId: moment.moduleId,
        instructor: moment.instructor,
        enabled: this.enabled,
        volume: this.volume,
        muted: this.muted,
        cacheStatus: resolved.cached ? "cached" : "fresh",
        backendResponseStatus: resolved.status,
        signedUrlExists: true,
        generationStatus: resolved.status,
        audioPlayStarted: false,
        audioPlayError: message || name,
        autoplayBlocked: false,
        firedState: this.played.has(momentId),
      });
      return { momentId, status: "failed", backendStatus: resolved.status, cached: resolved.cached };
    }
  }

  private hasCachedUrl(moment: InstructorVoiceMoment): boolean {
    const key = `${moment.moduleId}:${moment.momentId}:${moment.instructor}:${moment.transcript}`;
    return this.urlCache.has(key);
  }

  private async resolveUrl(
    moment: InstructorVoiceMoment,
    options: { poll: boolean; preload: boolean },
  ): Promise<VoiceUrlResolution> {
    const key = `${moment.moduleId}:${moment.momentId}:${moment.instructor}:${moment.transcript}`;
    const cached = this.urlCache.get(key);
    if (cached) return { url: cached, status: "ready", cached: true };

    const deadline = Date.now() + MAX_READY_WAIT_MS;
    let last: VoiceUrlResolution = { url: null, status: "missing", cached: false };

    do {
      const existing = this.inflight.get(key);
      const result = existing ?? this.requestSignedUrl(moment, key, options.preload);
      if (!existing) this.inflight.set(key, result);
      last = await result;
      if (last.url) return last;

      if (!options.poll) return last;
      if (last.status !== "generating") return last;
      if (Date.now() >= deadline) {
        return { ...last, status: last.status === "generating" ? "generating_timeout" : last.status };
      }
      await wait(POLL_INTERVAL_MS);
    } while (Date.now() < deadline);

    return { ...last, status: last.status === "generating" ? "generating_timeout" : last.status };
  }

  private async requestSignedUrl(
    moment: InstructorVoiceMoment,
    key: string,
    preload: boolean,
  ): Promise<VoiceUrlResolution> {
    try {
      const { data, error } = await supabase.functions.invoke("instructor-voice", {
        body: { action: "playback", moment_id: moment.momentId, preload },
      });
      if (error) return { url: null, status: "network_error", cached: false, error: error.message };
      const body = data;
      const url = body?.url as string | undefined;
      const status = (body?.status as string | undefined) ?? (url ? "ready" : "missing");
      const cached = Boolean(body?.cached);
      if (url) {
        this.urlCache.set(key, url);
        return { url, status: "ready", cached };
      }
      return { url: null, status, cached };
    } catch (e: unknown) {
      return { url: null, status: "network_error", cached: false, error: String(getErrorMessage(e) ?? e) };
    } finally {
      try {
        this.inflight.delete(key);
      } catch { /* ignore */ }
    }
  }
}

export const instructorVoice = new InstructorVoiceController();

export type { InstructorVoiceMoment };